/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in child component tree.
 * Prevents one component error from breaking the whole page.
 * Logs errors to backend for monitoring.
 */
import React from 'react';
import { AlertCircle, RefreshCw, BarChart3 } from 'lucide-react';
import errorLogger from '../utils/errorLogger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to backend
    const eventId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      eventId,
    });
    
    // Log to error tracking
    try {
      errorLogger.logError({
        type: 'ERROR_BOUNDARY',
        eventId,
        message: error?.message,
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        location: window.location.href,
        component: this.props.name || 'Unknown',
      });
    } catch (e) {
      console.error('Failed to log error:', e);
    }
    
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/sales-dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-[200px] flex items-center justify-center p-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Une erreur s'est produite
            </h2>
            
            <p className="text-gray-600 mb-4">
              {this.props.message || "Cette section n'a pas pu être chargée. L'erreur a été signalée automatiquement."}
            </p>
            
            {this.state.eventId && (
              <p className="text-xs text-gray-400 mb-4">
                Référence: {this.state.eventId}
              </p>
            )}
            
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Recharger
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <BarChart3 className="h-4 w-4" />
                Dashboard
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Détails techniques
                </summary>
                <pre className="mt-2 p-3 bg-gray-900 text-gray-100 rounded text-xs overflow-auto max-h-[200px]">
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export const withErrorBoundary = (Component, options = {}) => {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary name={options.name || Component.name} {...options}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary;
